import type { Ref, RefCallback } from 'react';

type RefCleanup = () => void;

const isObjectRef = <T>(
  ref: Ref<T> | undefined,
): ref is { current: T | null } =>
  typeof ref === 'object' && ref !== null && 'current' in ref;

/**
 * One callback ref that sets every given ref to the same node.
 * A callback ref may return a cleanup. This package also runs on React 18,
 * which warns if a callback ref returns a function, so the composed ref
 * keeps those cleanups and runs them on the detach call instead of
 * returning one itself.
 */
export const composeRefs = <T>(
  ...refs: Array<Ref<T> | undefined>
): RefCallback<T> => {
  const cleanups: RefCleanup[] = [];

  return (node) => {
    const previous = cleanups.splice(0);
    for (const cleanup of previous) cleanup();
    if (node == null) return;

    for (const ref of refs) {
      if (typeof ref === 'function') {
        const cleanup = ref(node) as void | RefCleanup;
        if (typeof cleanup === 'function') {
          cleanups.push(cleanup);
        } else {
          cleanups.push(() => {
            ref(null);
          });
        }
      } else if (isObjectRef<T>(ref)) {
        const objectRef = ref;
        objectRef.current = node;
        cleanups.push(() => {
          objectRef.current = null;
        });
      }
    }
  };
};
